import { MapPin, Navigation } from "lucide-react";
import { company } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { LocationButton } from "@/components/ActionButtons";
import { openWhatsApp } from "@/lib/whatsapp";

// Stylized dark map of the operating area with amber routes and animated pins.
const pins = [
  { id: "olbia", label: "Olbia", x: 300, y: 150, hq: true },
  { id: "golfo", label: "Golfo Aranci", x: 340, y: 120 },
  { id: "arzachena", label: "Arzachena", x: 305, y: 95 },
  { id: "portocervo", label: "Porto Cervo", x: 345, y: 80 },
  { id: "palau", label: "Palau", x: 300, y: 60 },
  { id: "santateresa", label: "S. Teresa", x: 255, y: 45 },
  { id: "tempio", label: "Tempio P.", x: 235, y: 95 },
  { id: "sanTeodoro", label: "San Teodoro", x: 320, y: 205 },
  { id: "budoni", label: "Budoni", x: 335, y: 235 },
  { id: "nuoro", label: "Nuoro", x: 250, y: 275 },
  { id: "sassari", label: "Sassari", x: 175, y: 120 },
  { id: "cagliari", label: "Cagliari", x: 220, y: 430 },
];

const routes = [
  ["olbia", "arzachena"], ["olbia", "sanTeodoro"], ["olbia", "tempio"],
  ["arzachena", "portocervo"], ["arzachena", "palau"], ["palau", "santateresa"],
  ["olbia", "golfo"], ["sanTeodoro", "budoni"], ["olbia", "nuoro"],
  ["olbia", "sassari"], ["nuoro", "cagliari"],
];

function pt(id) {
  return pins.find((p) => p.id === id);
}

export function OperationalArea() {
  const { t, lang } = useLang();

  const checkAvailability = () => {
    const msg =
      lang === "en"
        ? "Hi EMME SERVICE CAR, I'd like to check availability for my area. My location is: [TO FILL]"
        : "Ciao EMME SERVICE CAR, vorrei verificare la disponibilità nella mia zona. La mia posizione è: [DA COMPILARE]";
    openWhatsApp(msg);
  };

  return (
    <section id="area" data-testid="map-section" className="relative bg-asphalt py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <Overline>{t.map.overline}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl lg:text-5xl">
            {t.map.title}
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-steel">{t.map.subtitle}</p>

          <div className="mt-6 flex flex-wrap gap-2">
            {company.localities.map((loc) => (
              <span key={loc} className="rounded-full border border-edge bg-carbon px-3 py-1.5 text-xs font-medium text-steel">
                {loc}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <LocationButton testId="map-location-btn" variant="solid" />
            <button
              type="button"
              onClick={checkAvailability}
              data-testid="map-availability-btn"
              className="inline-flex items-center gap-2 rounded-md border border-amber px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-amber transition-colors hover:bg-amber/10 active:scale-95"
            >
              <Navigation className="h-4 w-4" /> {t.common.checkAvailability}
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative overflow-hidden rounded-2xl border border-edge bg-carbon p-4">
            <svg viewBox="0 0 480 500" className="h-auto w-full" role="img" aria-label={t.map.title}>
              <defs>
                <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                  <path d="M24 0H0V24" fill="none" stroke="#2A313C" strokeWidth="0.5" opacity="0.4" />
                </pattern>
              </defs>
              <rect width="480" height="500" fill="url(#grid)" />
              {/* stylized Sardinia silhouette */}
              <path
                d="M235 30 Q290 35 300 75 Q350 70 355 110 Q345 150 320 165 Q345 200 340 245 Q320 300 275 300 Q265 360 235 400 Q245 450 210 460 Q160 445 165 390 Q140 330 175 285 Q150 230 165 175 Q145 130 175 105 Q195 55 235 30 Z"
                fill="#15191E"
                stroke="#2A313C"
                strokeWidth="1.5"
              />

              {routes.map(([a, b], i) => {
                const p1 = pt(a), p2 = pt(b);
                return (
                  <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#F6B800" strokeWidth="1.2" strokeOpacity="0.55" strokeDasharray="3 3" />
                );
              })}

              {pins.map((p) => (
                <g key={p.id}>
                  {p.hq ? (
                    <>
                      <circle cx={p.x} cy={p.y} r="7" className="animate-pin-pulse" fill="#F6B800" />
                      <circle cx={p.x} cy={p.y} r="5" fill="#F6B800" />
                      <text x={p.x + 11} y={p.y + 4} fill="#F4F7F8" fontSize="12" fontWeight="700" fontFamily="Sora, sans-serif">{p.label}</text>
                    </>
                  ) : (
                    <>
                      <circle cx={p.x} cy={p.y} r="6" className="animate-pin-pulse" fill="#2F7BFF" style={{ animationDelay: `${(p.x % 5) * 0.3}s` }} />
                      <circle cx={p.x} cy={p.y} r="3.2" fill="#2F7BFF" />
                      <text x={p.x + 8} y={p.y + 3.5} fill="#818A93" fontSize="10" fontFamily="Inter, sans-serif">{p.label}</text>
                    </>
                  )}
                </g>
              ))}
            </svg>

            <div className="mt-2 flex items-center gap-4 px-1 text-xs text-steel">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber" /> {t.map.hq}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-signal" /> {company.areaShort}</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
